import { createContext, useContext, useState, type ReactNode } from "react";
import { DefaultStepValues, PossibleStepsLabels } from "../lib/step.constants";
import type { CustomStep } from "../types/types";

type StateContextType = {
  steps: CustomStep[];
  setSteps: React.Dispatch<React.SetStateAction<CustomStep[]>>;
  onNextStep: (newTitle: string) => void;
  handleBack: () => void;
  goStep: (type: keyof typeof PossibleStepsLabels) => void;
};

export const StepContext = createContext<undefined | StateContextType>(undefined);

export const useSteps = () => {
  const stepValues = useContext<StateContextType | undefined>(StepContext);
  if (!stepValues) throw new Error("useSteps must be used within <StepProvider>");

  return stepValues;
};

export const StepProvider = ({ children }: { children: ReactNode }) => {
  const [steps, setSteps] = useState<CustomStep[]>([...DefaultStepValues.specialtyFirst]);

  const onNextStep = (newTitle: string) => {
    let nextStep = -1;

    setSteps((prev) =>
      prev.map((step, index) => {
        if (step.status === "process") {
          nextStep = index + 1;
          return {
            ...step,
            title: `${newTitle}`,
            status: "finish",
          };
        }
        if (index === nextStep) {
          return {
            ...step,
            status: "process",
          };
        }
        return step;
      }),
    );
  };

  const goStep = (type: string) => {
    let currStepIndex = -1;

    setSteps((prev) => {
      let newSteps = prev;
      newSteps = newSteps.map((step, index) => {
        if (step.type === type) {
          currStepIndex = index;
          return {
            ...step,
            status: "process",
          };
        }
        return step;
      });
      newSteps = newSteps.map((step, index) => {
        if (index > currStepIndex && currStepIndex !== -1) {
          return {
            ...step,
            status: "wait",
          };
        }
        if (index < currStepIndex && currStepIndex !== -1) {
          return {
            ...step,
            status: "finish",
          };
        }
        return step;
      });
      return newSteps;
    });
  };

  const handleBack = () => {
    let currStepIndex = -1;
    setSteps((prev) => {
      let newSteps = prev;
      newSteps = newSteps.map((step, index) => {
        if (step.status === "process") {
          currStepIndex = index;
          return {
            ...step,
            status: "wait",
          };
        }
        return step;
      });
      newSteps = newSteps.map((step, index) => {
        if (index === currStepIndex - 1) {
          return {
            ...step,
            status: "process",
          };
        }
        return step;
      });
      return newSteps;
    });
  };

  return (
    <StepContext.Provider value={{ steps, setSteps, onNextStep, goStep, handleBack }}>{children}</StepContext.Provider>
  );
};
